import React, { useState, useEffect } from 'react';
import { FaSearch, FaFileExcel, FaFilter } from 'react-icons/fa';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const exportToCSV = (rows, filename) => {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

function LogsPage() {
  const { getAdminToken } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterAdmin, setFilterAdmin] = useState('all');
  const [admins, setAdmins] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchAdmins();

    // Real-time subscription
    const subscription = supabase
      .channel('system_logs_changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'system_logs' },
        (payload) => {
          console.log('New log entry:', payload);
          fetchLogs();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterLogs();
  }, [searchTerm, filterAction, filterAdmin, logs]);

  const fetchLogs = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_get_system_logs', { p_admin_token: getAdminToken() });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);
      setLogs((data.rows || []).map(r => ({ ...r, admins: { full_name: r.admin_full_name, email: r.admin_email } })));
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      const { data, error } = await supabase.rpc('admin_list_admins', { p_admin_token: getAdminToken() });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error);
      setAdmins(data.rows || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  const filterLogs = () => {
    let filtered = logs;

    // Search
    if (searchTerm) {
      filtered = filtered.filter(
        (log) =>
          log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.target_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.admins?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          JSON.stringify(log.details).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Action filter
    if (filterAction !== 'all') {
      filtered = filtered.filter((log) => log.action === filterAction);
    }

    // Admin filter
    if (filterAdmin !== 'all') {
      filtered = filtered.filter((log) => log.admin_id === filterAdmin);
    }

    setFilteredLogs(filtered);
  };

  const getActionColor = (action) => {
    const actionColors = {
      login: '#2196F3',
      logout: '#9E9E9E',
      kill_switch: '#F44336',
      activate: '#4CAF50',
      ban_user: '#F44336',
      unban_user: '#4CAF50',
      ban_user_fraud: '#D32F2F',
      edit_points: '#FF9800',
      delete_user: '#D32F2F',
      resolve_fraud: '#4CAF50',
    };
    return actionColors[action] || '#666';
  };

  const getActionLabel = (action) => {
    const labels = {
      login: '🔓 Login',
      logout: '🔒 Logout',
      kill_switch: '🔴 Kill Switch',
      activate: '✅ Activate',
      ban_user: '🚫 Ban User',
      unban_user: '✅ Unban User',
      ban_user_fraud: '🚨 Ban (Fraud)',
      edit_points: '✏️ Edit Points',
      delete_user: '🗑️ Delete User',
      resolve_fraud: '✅ Resolve Fraud',
    };
    return labels[action] || action;
  };

  const getTargetTypeLabel = (type) => {
    const labels = {
      admin: '👤 Admin',
      user: '👥 User',
      business: '🏢 Business',
      fraud: '🚨 Fraud',
      system: '⚙️ System',
    };
    return labels[type] || type;
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleString('az-AZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    if (seconds < 60) return `${seconds} saniyə əvvəl`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} dəqiqə əvvəl`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} saat əvvəl`;
    return `${Math.floor(seconds / 86400)} gün əvvəl`;
  };

  const handleExport = () => {
    exportToCSV(filteredLogs.map(log => ({
      created_at: log.created_at,
      admin: log.admins?.full_name || 'System',
      action: log.action,
      target_type: log.target_type,
      target_id: log.target_id,
      details: JSON.stringify(log.details),
      ip_address: log.ip_address,
    })), 'system_logs.csv');
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Yüklənir...</p>
      </div>
    );
  }

  // Action statistics
  const actionStats = logs.reduce((acc, log) => {
    acc[log.action] = (acc[log.action] || 0) + 1;
    return acc;
  }, {});

  const uniqueActions = [...new Set(logs.map((log) => log.action))];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📜 Sistem Loqları</h1>
          <p style={styles.subtitle}>Bütün admin action-larını izləyin</p>
        </div>
        <button onClick={handleExport} style={styles.btnExport}>
          <FaFileExcel /> Export CSV
        </button>
      </div>

      {/* Stats */}
      <div style={styles.stats}>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Ümumi Loq:</span>
          <span style={styles.statValue}>{logs.length}</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Bu gün:</span>
          <span style={styles.statValue}>
            {logs.filter((log) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return new Date(log.created_at) >= today;
            }).length}
          </span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Son 1 saat:</span>
          <span style={styles.statValue}>
            {
              logs.filter((log) => {
                const oneHourAgo = new Date(Date.now() - 3600000);
                return new Date(log.created_at) >= oneHourAgo;
              }).length
            }
          </span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statLabel}>Aktiv adminlər:</span>
          <span style={styles.statValue}>
            {new Set(logs.map((log) => log.admin_id)).size}
          </span>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.searchBox}>
          <FaSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Action, admin və ya details ilə axtarış..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <div style={styles.filterGroup}>
          <FaFilter style={styles.filterIcon} />
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Bütün Action-lar</option>
            {uniqueActions.map((action) => (
              <option key={action} value={action}>
                {getActionLabel(action)} ({actionStats[action]})
              </option>
            ))}
          </select>

          <select
            value={filterAdmin}
            onChange={(e) => setFilterAdmin(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Bütün Adminlər</option>
            {admins.map((admin) => (
              <option key={admin.id} value={admin.id}>
                {admin.full_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Vaxt</th>
              <th style={styles.th}>Admin</th>
              <th style={styles.th}>Action</th>
              <th style={styles.th}>Target</th>
              <th style={styles.th}>Details</th>
              <th style={styles.th}>IP</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log) => (
              <tr key={log.id} style={styles.row}>
                <td style={styles.td}>
                  <div style={styles.timeCell}>
                    <span style={styles.timeMain}>{formatTime(log.created_at)}</span>
                    <span style={styles.timeAgo}>{getTimeAgo(log.created_at)}</span>
                  </div>
                </td>
                <td style={styles.td}>
                  <strong>{log.admins?.full_name || 'System'}</strong>
                </td>
                <td style={styles.td}>
                  <span
                    style={{
                      ...styles.actionBadge,
                      backgroundColor: `${getActionColor(log.action)}20`,
                      color: getActionColor(log.action),
                    }}
                  >
                    {getActionLabel(log.action)}
                  </span>
                </td>
                <td style={styles.td}>
                  {log.target_type && (
                    <span style={styles.targetBadge}>
                      {getTargetTypeLabel(log.target_type)}
                    </span>
                  )}
                </td>
                <td style={styles.td}>
                  {log.details && (
                    <details style={styles.details}>
                      <summary style={styles.detailsSummary}>Bax</summary>
                      <pre style={styles.detailsContent}>
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </details>
                  )}
                </td>
                <td style={styles.td}>
                  <span style={styles.ipAddress}>{log.ip_address || 'N/A'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredLogs.length === 0 && (
          <div style={styles.noData}>
            <p>Heç bir loq tapılmadı</p>
          </div>
        )}
      </div>

      {/* Pagination Info */}
      <div style={styles.paginationInfo}>
        <p>
          Göstərilir: {filteredLogs.length} / {logs.length} loq
        </p>
        {logs.length >= 200 && (
          <p style={styles.limitWarning}>
            ⚠️ Yalnız son 200 loq göstərilir
          </p>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1800px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1A1A2E',
    marginBottom: '5px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
  },
  btnExport: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#4CAF50',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '30px',
  },
  statCard: {
    padding: '15px 20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '600',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1A1A2E',
  },
  filters: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  searchBox: {
    flex: 1,
    minWidth: '300px',
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: '15px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#999',
  },
  searchInput: {
    width: '100%',
    padding: '12px 15px 12px 45px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
  },
  filterGroup: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  filterIcon: {
    color: '#999',
    fontSize: '16px',
  },
  filterSelect: {
    padding: '12px 15px',
    fontSize: '14px',
    border: '2px solid #E0E0E0',
    borderRadius: '8px',
    outline: 'none',
    cursor: 'pointer',
    backgroundColor: '#fff',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    overflow: 'auto',
    marginBottom: '20px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '1200px',
  },
  headerRow: {
    backgroundColor: '#F8F9FA',
  },
  th: {
    padding: '15px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#666',
    textTransform: 'uppercase',
    borderBottom: '2px solid #E0E0E0',
  },
  row: {
    borderBottom: '1px solid #F0F0F0',
  },
  td: {
    padding: '15px',
    fontSize: '14px',
    color: '#1A1A2E',
    verticalAlign: 'top',
  },
  timeCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  timeMain: {
    fontSize: '13px',
    color: '#1A1A2E',
    fontWeight: '500',
  },
  timeAgo: {
    fontSize: '11px',
    color: '#999',
  },
  actionBadge: {
    display: 'inline-block',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  targetBadge: {
    display: 'inline-block',
    padding: '5px 12px',
    backgroundColor: '#F8F9FA',
    color: '#666',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  details: {
    cursor: 'pointer',
  },
  detailsSummary: {
    color: '#2196F3',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  detailsContent: {
    marginTop: '10px',
    padding: '10px',
    backgroundColor: '#F8F9FA',
    borderRadius: '8px',
    fontSize: '11px',
    fontFamily: 'monospace',
    color: '#666',
    maxWidth: '300px',
    overflow: 'auto',
  },
  ipAddress: {
    fontFamily: 'monospace',
    fontSize: '12px',
    color: '#666',
  },
  noData: {
    padding: '40px',
    textAlign: 'center',
    color: '#999',
  },
  paginationInfo: {
    textAlign: 'center',
    color: '#666',
    fontSize: '14px',
  },
  limitWarning: {
    color: '#F57C00',
    marginTop: '5px',
    fontWeight: '600',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #FF6B35',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

export default LogsPage;